/**
 * SearchFilterChips — V107.52.34
 *
 * Horizontal pill row above search results. "All" + 7 entity-type chips
 * with live count badges. Tapping a chip filters the results to that
 * entity type only.
 */

import { motion } from 'framer-motion';
import {
  Search as SearchIcon,
  User,
  Contact as ContactIcon,
  Sparkles,
  AlertTriangle,
  Briefcase,
  MessageSquare,
  Wifi,
  Tag,
} from 'lucide-react';
import type { SearchFilter, SearchEntityType } from '@/hooks/useGlobalSearch';

interface ChipDef {
  key: SearchFilter;
  label: string;
  icon: typeof User;
  countKey?: SearchEntityType;
  /** Tailwind text color when active */
  activeColor: string;
}

const CHIPS: ChipDef[] = [
  { key: 'all', label: 'All', icon: SearchIcon, activeColor: 'text-brand-secondary' },
  { key: 'customer', label: 'Customers', icon: User, countKey: 'customer', activeColor: 'text-sky-300' },
  { key: 'contact', label: 'Contacts', icon: ContactIcon, countKey: 'contact', activeColor: 'text-teal-300' },
  { key: 'deal', label: 'Deals', icon: Sparkles, countKey: 'deal', activeColor: 'text-amber-300' },
  { key: 'fault', label: 'Faults', icon: AlertTriangle, countKey: 'fault', activeColor: 'text-rose-300' },
  { key: 'job', label: 'Jobs', icon: Briefcase, countKey: 'job', activeColor: 'text-purple-300' },
  {
    key: 'service_request',
    label: 'Requests',
    icon: MessageSquare,
    countKey: 'service_request',
    activeColor: 'text-violet-300',
  },
  {
    key: 'customer_service',
    label: 'Services',
    icon: Wifi,
    countKey: 'customer_service',
    activeColor: 'text-emerald-300',
  },
  { key: 'serial', label: 'Serials', icon: Tag, countKey: 'serial', activeColor: 'text-cyan-300' },
];

interface Props {
  active: SearchFilter;
  onSelect: (filter: SearchFilter) => void;
  counts: Record<SearchEntityType, number>;
  totalCount: number;
}

export function SearchFilterChips({ active, onSelect, counts, totalCount }: Props) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
      {CHIPS.map((chip) => {
        const isActive = active === chip.key;
        const count = chip.key === 'all' ? totalCount : (chip.countKey ? counts[chip.countKey] : 0);
        const ChipIcon = chip.icon;

        // Hide chips with zero results when not active (keeps the bar clean
        // for quiet queries) — except "All", which is always visible.
        if (chip.key !== 'all' && !isActive && count === 0) return null;

        return (
          <button
            key={chip.key}
            type="button"
            onClick={() => onSelect(chip.key)}
            className={`relative shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
              isActive
                ? `${chip.activeColor} bg-surface-hover border-theme`
                : 'text-theme-tertiary border-theme-soft hover:text-theme-secondary hover:bg-surface-hover/60'
            }`}
          >
            <ChipIcon className="w-3.5 h-3.5" />
            <span>{chip.label}</span>
            {count > 0 && (
              <span
                className={`text-[10px] tabular-nums px-1.5 rounded-full ${
                  isActive ? 'bg-brand-secondary/20 text-brand-secondary' : 'bg-surface-hover text-theme-muted'
                }`}
              >
                {count}
              </span>
            )}
            {isActive && (
              <motion.div
                layoutId="searchChipIndicator"
                className="absolute inset-0 rounded-full ring-1 ring-brand-secondary/30 pointer-events-none"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
